import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Button from '../components/Button';
import { BookIcon, ClipboardIcon, CameraIcon } from '../components/Icons';
import { getPageMeta } from '../utils/seoHelmet';
// import { trustedClients, trustBarTitle } from '../data';

const Home = () => {
    const [isValuePropVisible, setIsValuePropVisible] = useState(false);
    const [isServicesVisible, setIsServicesVisible] = useState(false);
    const [isWhyAiMattersVisible, setIsWhyAiMattersVisible] = useState(false);

    const valuePropRef = useRef(null);
    const servicesRef = useRef(null);
    const whyAiMattersRef = useRef(null);

    // Value proposition section animation
    useEffect(() => {
        const currentRef = valuePropRef.current;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    setIsValuePropVisible(entry.isIntersecting);
                });
            },
            {
                threshold: 0.15,
                rootMargin: '0px 0px -100px 0px'
            }
        );

        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, []);

    // Services section animation
    useEffect(() => {
        const currentRef = servicesRef.current;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    setIsServicesVisible(entry.isIntersecting);
                });
            },
            {
                threshold: 0.1,
                rootMargin: '0px 0px -50px 0px'
            }
        );

        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, []);

    // Why AI Matters section animation
    useEffect(() => {
        const currentRef = whyAiMattersRef.current;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    setIsWhyAiMattersVisible(entry.isIntersecting);
                });
            },
            {
                threshold: 0.15,
                rootMargin: '0px 0px -100px 0px'
            }
        );

        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, []);

    const services = [
        {
            icon: <BookIcon size={28} />,
            title: 'AI Enablement & Training',
            description: 'Corporate-focused AI training designed to build AI literacy, practical capability, and leadership readiness across modern organizations.',
            features: [
                'AI Native, ASCEND & P.R.O.M.P.T. frameworks',
                'Practical task automation & augmentation',
                'Role-based, business-relevant use cases',
                'Responsible & governance-aware AI adoption'
            ]
        },
        {
            icon: <ClipboardIcon size={28} />,
            title: 'ISO 9001:2015 Quality Management Consultancy',
            description: 'End-to-end ISO 9001 consultancy to help organizations design, implement, and prepare a Quality Management System that is compliant, measurable, and aligned with business objectives.',
            features: [
                'QMS planning, documentation & implementation',
                'Process mapping & risk-based thinking',
                'Internal audit & management review readiness',
                'Certification-focused, audit-ready approach'
            ]
        },
        {
            icon: <ClipboardIcon size={28} />,
            title: 'ISO/IEC 27001:2022 Information Security Consultancy',
            description: 'Comprehensive ISMS consultancy covering the full ISO/IEC 27001 lifecycle—from risk assessment to certification readiness—aligned with Annex A controls.',
            features: [
                'ISMS scope definition & gap assessment',
                'Risk assessment, treatment & SoA development',
                'Security policies, procedures & governance',
                'Internal audit & certification readiness support'
            ]
        },
        {
            icon: <CameraIcon size={28} />,
            title: 'AI-Powered CCTV Prevention Platform',
            description: 'An enterprise-grade AI-driven surveillance platform designed for real-time threat detection, behavioral analysis, and proactive prevention.',
            features: [
                'Real-time threat & anomaly detection',
                'Enterprise system integration',
                'Customizable alert thresholds',
                '24/7 monitoring with compliant data handling'
            ]
        }
    ];

    const meta = getPageMeta('home');

    return (
        <main>
            <Helmet>
                <title>{meta.title}</title>
                <meta name="description" content={meta.description} />
                <meta name="keywords" content={meta.keywords} />
                <meta name="robots" content={meta.robots} />
                <link rel="canonical" href={meta.canonical} />
                <meta property="og:title" content={meta.title} />
                <meta property="og:description" content={meta.description} />
                <meta property="og:image" content={meta.ogImage} />
                <meta property="og:url" content={meta.canonical} />
            </Helmet>
            {/* Hero Section */}
            <section
                className="hero hero--bg"
                style={{
                    backgroundImage:
                        "linear-gradient(to bottom, rgba(20, 20, 20, 0.7), rgba(20, 20, 20, 0.85)), url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&h=1080&fit=crop&q=80')"
                }}
            >
                <div className="container hero__container hero__container--centered">
                    <div className="hero__content hero__content--centered">
                        <span className="hero__eyebrow hero__eyebrow--light">Phoenix AI</span>
                        <h1 className="hero__title hero__title--light">The Intelligence Layer Of Tomorrow</h1>
                        <p className="hero__subtitle hero__subtitle--light">
                            Phoenix AI delivers AI enablement, ISO consultancy, and enterprise security solutions for organizations that demand compliance, resilience, and measurable results. Trusted by SMEs and listed companies.
                        </p>
                        <div className="hero__actions">
                            <Button to="/contact" size="large">
                                Get in Touch
                            </Button>
                            <Button to="/services" variant="outline" size="large">
                                Our Services
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Preview */}
            <section className={`section services-preview ${isServicesVisible ? 'services-preview--visible' : ''}`} ref={servicesRef}>
                <div className="container">
                    <div className="services-preview__header">
                        <span className="services-preview__eyebrow">What We Offer</span>
                        <h2 className="services-preview__title">Enterprise-Grade Intelligence, Compliance & Security Solutions</h2>
                        <p className="services-preview__subtitle">
                            Phoenix AI delivers structured, audit-ready, and business-aligned solutions that help organizations adopt AI responsibly, achieve international standards, and protect critical assets—without compromising operational efficiency.
                        </p>
                    </div>
                    <div className="services-preview__list">
                        {services.map((service, index) => (
                            <div key={index} className="services-preview__item">
                                <div className="services-preview__item-left">
                                    <span className="services-preview__item-number">{String(index + 1).padStart(2, '0')}</span>
                                    <h3 className="services-preview__item-title">{service.title}</h3>
                                </div>
                                <div className="services-preview__item-right">
                                    <p className="services-preview__item-text">{service.description}</p>
                                    <div className="services-preview__item-features">
                                        {service.features.map((feature, idx) => (
                                            <span key={idx} className="services-preview__item-tag">
                                                {feature}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Value Proposition */}
            <section className={`value-prop ${isValuePropVisible ? 'value-prop--visible' : ''}`} ref={valuePropRef}>
                <div className="container">
                    <div className="value-prop__header">
                        <span className="value-prop__eyebrow">Why Choose Us</span>
                        <h2 className="value-prop__title">Why Organizations Choose Phoenix AI</h2>
                        <p className="value-prop__intro">
                            Phoenix AI partners with organizations to build intelligent, compliant, and secure operations—combining AI enablement, international standards, and enterprise-grade security into a single, trusted delivery model.
                        </p>
                    </div>
                    <div className="value-prop__grid">
                        <div className="value-prop__card">
                            <h3 className="value-prop__card-title">Business-First, Technology-Driven</h3>
                            <p className="value-prop__card-text">
                                We focus on real business outcomes—productivity, risk reduction, and operational resilience—supported by practical AI and proven methodologies.
                            </p>
                        </div>
                        <div className="value-prop__card">
                            <h3 className="value-prop__card-title">Structured, Evidence-Based Delivery</h3>
                            <p className="value-prop__card-text">
                                Our solutions follow clear frameworks, documented processes, and measurable milestones—ensuring accountability across training, consulting, and platform deployments.
                            </p>
                        </div>
                        <div className="value-prop__card">
                            <h3 className="value-prop__card-title">Integrated Intelligence, Quality & Security</h3>
                            <p className="value-prop__card-text">
                                From AI skills development and governance frameworks to ISO-aligned systems and intelligent surveillance, our services are designed to work together—not in silos.
                            </p>
                        </div>
                        <div className="value-prop__card">
                            <h3 className="value-prop__card-title">Enterprise-Ready, Scalable for Growth</h3>
                            <p className="value-prop__card-text">
                                Built for SMEs, trusted by enterprises—our approach scales with your organization while maintaining control, compliance, and security.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Intelligent, Compliant & Secure Systems Matter */}
            <section className={`section why-ai-matters ${isWhyAiMattersVisible ? 'why-ai-matters--visible' : ''}`} ref={whyAiMattersRef}>
                <div className="container">
                    <div className="why-ai-matters__header">
                        <span className="why-ai-matters__eyebrow">Understanding the Landscape</span>
                        <h2 className="why-ai-matters__title">Why Intelligent, Compliant & Secure Systems Matter</h2>
                        <p className="why-ai-matters__subtitle">
                            As organizations accelerate digital and AI adoption, success is no longer defined by innovation alone—but by how responsibly, securely, and sustainably intelligence is embedded into operations.
                        </p>
                    </div>
                    <div className="why-ai-matters__grid">
                        <div className="why-ai-matters__card">
                            <div className="why-ai-matters__card-media">
                                <img src="https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?auto=format&fit=crop&w=900&q=80" alt="Team collaborating on AI strategy" />
                                <span className="why-ai-matters__card-badge">01</span>
                            </div>
                            <h3 className="why-ai-matters__card-title">Intelligent Adoption, Not Just Automation</h3>
                            <p className="why-ai-matters__card-text">
                                AI is rapidly reshaping how work is done. Organizations need structured AI enablement—clear use cases, skilled people, and governance—to ensure AI delivers productivity without introducing operational risk.
                            </p>
                        </div>
                        <div className="why-ai-matters__card">
                            <div className="why-ai-matters__card-media">
                                <img src="https://images.unsplash.com/photo-1556740749-887f6717d7e4?auto=format&fit=crop&w=900&q=80" alt="Compliance and quality management documentation" />
                                <span className="why-ai-matters__card-badge">02</span>
                            </div>
                            <h3 className="why-ai-matters__card-title">Compliance as a Business Enabler</h3>
                            <p className="why-ai-matters__card-text">
                                International standards such as ISO 9001 and ISO/IEC 27001 provide the foundation for consistency, accountability, and trust in technology-driven organizations.
                            </p>
                        </div>
                        <div className="why-ai-matters__card">
                            <div className="why-ai-matters__card-media">
                                <img src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=900&q=80" alt="Security operations center monitoring risks" />
                                <span className="why-ai-matters__card-badge">03</span>
                            </div>
                            <h3 className="why-ai-matters__card-title">Security & Risk Are Enterprise Priorities</h3>
                            <p className="why-ai-matters__card-text">
                                From information systems to physical environments, modern enterprises must manage cyber, data, and operational risks through integrated security controls and continuous monitoring.
                            </p>
                        </div>
                        <div className="why-ai-matters__card">
                            <div className="why-ai-matters__card-media">
                                <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=900&q=80" alt="Security monitoring dashboard and surveillance systems" />
                                <span className="why-ai-matters__card-badge">04</span>
                            </div>
                            <h3 className="why-ai-matters__card-title">Proactive Prevention Over Reactive Response</h3>
                            <p className="why-ai-matters__card-text">
                                Traditional, reactive approaches to quality, security, and surveillance are no longer sufficient. Intelligent, preventive systems create resilience and long-term competitive advantage.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Home;
